import queryString from 'query-string';
import { callApi } from './api';

let staffJwt = null;
let authenticationRevision = 0;

const request = async (
  base,
  path,
  method,
  body,
  requireStaff = true,
  credentials
) => {
  if (requireStaff && !staffJwt) {
    const result = await callApi(
      `${base}${path}`,
      method,
      body,
      null,
      false,
      undefined,
      credentials
    );
    if (!result.error) return result;
    const { response, json } = result.error;
    return {
      error: {
        status: response && response.status,
        code: json.code,
        message: json.error || json.message || 'Learning action failed.',
        skillName: json.skillName,
        currentGeneration: json.currentGeneration,
        currentSha256: json.currentSha256,
        affectedVariants: json.affectedVariants,
      },
    };
  }
  const requestJwt = staffJwt;
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
  try {
    const response = await fetch(`${base}${path}`, {
      method,
      ...(credentials ? { credentials } : {}),
      headers: {
        Accept: 'application/json',
        ...(!multipart ? { 'Content-Type': 'application/json' } : {}),
        ...(requireStaff ? { Authorization: `Bearer ${requestJwt}` } : {}),
      },
      ...(body ? { body: multipart ? body : JSON.stringify(body) } : {}),
    });
    const json = await response.json();
    if (!response.ok) {
      if (
        (response.status === 401 || json.code === 'STAFF_REAUTH_REQUIRED') &&
        staffJwt === requestJwt
      )
        staffJwt = null;
      return {
        error: {
          status: response.status,
          code: json.code,
          message: json.error || json.message || 'Staff action failed.',
          skillName: json.skillName,
          currentGeneration: json.currentGeneration,
          currentSha256: json.currentSha256,
          affectedVariants: json.affectedVariants,
        },
      };
    }
    return { response: json };
  } catch (error) {
    return {
      error: { message: 'Unable to reach the staff learning service.' },
    };
  }
};

const backend = (path, method, body, requireStaff) =>
  request(process.env.REACT_APP_API_URL, path, method, body, requireStaff);
const filematch = (path, method = 'GET', body) =>
  request(process.env.REACT_APP_FILEMATCH_URL, path, method, body);

export const callStaffFileMatchApi = (
  endpoint,
  companyId,
  method = 'GET',
  body,
  query = {}
) => {
  if (!companyId)
    return Promise.resolve({
      error: {
        message: 'Select the target company before editing its catalog.',
      },
    });
  return filematch(
    `${endpoint}?${queryString.stringify({ ...query, companyId })}`,
    method,
    body
  );
};

export const learningApi = {
  signIn: async ({ email, password }) => {
    staffJwt = null;
    const revision = ++authenticationRevision;
    const result = await backend('/signin', 'POST', { email, password }, false);
    if (revision !== authenticationRevision)
      return {
        error: { message: 'Staff sign-in was cancelled. Sign in again.' },
      };
    if (result.error) return result;
    if (
      !result.response.loggedIn ||
      typeof result.response.jwt !== 'string' ||
      !result.response.jwt
    ) {
      return {
        error: { message: result.response.err || 'Staff sign-in failed.' },
      };
    }
    staffJwt = result.response.jwt;
    return { response: { userId: result.response.userId } };
  },
  signOut: () => {
    staffJwt = null;
    authenticationRevision += 1;
  },
  createPreparationSession: (companyId, operationId) =>
    backend('/internal/learning/preparation-sessions', 'POST', {
      companyId,
      ...(operationId !== undefined ? { operationId } : {}),
    }),
  openResolutionSession: async ({ companyId, preparationId }) => {
    const gateway = process.env.REACT_APP_HERMES_GATEWAY_URL;
    if (!gateway || !companyId || !preparationId)
      return {
        error: {
          message:
            'A configured gateway and current preparation session are required.',
        },
      };
    const result = await request(
      gateway,
      '/api/session',
      'POST',
      {
        purpose: 'learning-resolution',
        companyId,
        preparationId,
      },
      true,
      'include'
    );
    if (result.error) return result;
    try {
      const destination = new URL(result.response.chatUrl, gateway);
      if (
        destination.origin !== new URL(gateway).origin ||
        destination.pathname !==
          `/learning/c/${encodeURIComponent(companyId)}/chat` ||
        destination.search ||
        destination.hash ||
        destination.username ||
        destination.password
      )
        return {
          error: {
            message: 'The resolution session returned an invalid destination.',
          },
        };
      return { response: { chatUrl: destination.href } };
    } catch (error) {
      return {
        error: {
          message: 'The resolution session returned an invalid destination.',
        },
      };
    }
  },
  getAction: (preparedActionId, companyId) =>
    filematch(
      `/ai-settings/learning/actions/${encodeURIComponent(
        preparedActionId
      )}?${queryString.stringify({ companyId })}`
    ),
  prepareMigration: (preparationId, companyId, body) =>
    filematch(
      `/ai-settings/learning/actions/${encodeURIComponent(
        preparationId
      )}/prepare?${queryString.stringify({ companyId })}`,
      'POST',
      body
    ),
  confirmAction: ({ preparedActionId, expectedActionDigest, companyId }) =>
    filematch(
      `/ai-settings/learning/actions/${encodeURIComponent(
        preparedActionId
      )}/confirm?${queryString.stringify({ companyId })}`,
      'POST',
      { expectedActionDigest }
    ),
};
